
import { configureStore } from '@reduxjs/toolkit';

// Import reducers with explicit naming to avoid initialization issues
import { contentReducer } from './slices/content/contentSlice';
import authReducer from './slices/auth/authSlice';
import contactsReducer from './slices/contacts/contactsSlice';
import companiesReducer from './slices/companies/companiesSlice';
import tagsReducer from './slices/tagsSlice';
import { emailChannelsReducer } from './slices/emailChannels/emailChannelsSlice';
import widgetReducer from './slices/widget/widgetSlice';
import actionsReducer from './slices/actions/actionsSlice';
import teammatesReducer from './slices/teammates/teammatesSlice';
import { cannedResponsesReducer } from './slices/cannedResponses/cannedResponsesSlice';
import { chatbotsReducer } from './slices/chatbots/chatbotsSlice';
import inboxReducer from './slices/inboxSlice';
import { copilotProfilesReducer } from './slices/copilot-profiles/copilotProfilesSlice';
import copilotReducer from './slices/copilot/copilotSlice';
import copilotChatReducer from './slices/copilot/copilotChatSlice';
import teamsReducer from './slices/teams/teamsSlice';
import contentUiReducer from './slices/content/contentUiSlice';
import folderReducer from './slices/folder/folderSlice';
import { userReducer } from './slices/user/userSlice';
import actionsUiReducer from './slices/actions/actionsUiSlice';
import actionFolderReducer from './slices/actions/actionFolderSlice';
import customersReducer from './slices/customers/customersSlice';
import notificationReducer from './slices/notificationSlice';
import workflowFolderReducer from './slices/workflow/workflowFolderSlice';
import workflowsReducer from './slices/workflow/workflowSlice';
import channelsReducer from './slices/channels/channelsSlice';
import { ticketStatusesReducer } from './slices/ticketStatuses/ticketStatusesSlice';
import { ticketPrioritiesReducer } from './slices/ticketPriorities/ticketPrioritiesSlice';
import autoReplyReducer from './slices/autoReplySlice';
import { ticketsReducer } from './slices/tickets/ticketsSlice';
import { ticketPagesReducer } from './slices/ticketPages/ticketPagesSlice';
import workspaceSettingsReducer from './slices/workspaceSettings/workspaceSettingsSlice';
import agentStatusesReducer from './slices/agentStatuses/agentStatusesSlice';
import { conversationsReducer } from './slices/conversations/conversationsSlice';
import emailTemplatesReducer from './slices/emailTemplates/emailTemplatesSlice';

export const store = configureStore({
  reducer: {
    // Content and folders
    content: contentReducer,
    contentUi: contentUiReducer,
    folder: folderReducer,

    // Auth and user
    auth: authReducer,
    user: userReducer,

    // Customers
    customers: customersReducer,

    // Contacts and companies
    contacts: contactsReducer,
    companies: companiesReducer,

    // Teams, tags, and other organizational structures
    teams: teamsReducer,
    tags: tagsReducer,
    teammates: teammatesReducer,

    // Communication and integration
    emailChannels: emailChannelsReducer,
    channels: channelsReducer,
    widget: widgetReducer,

    // Actions center
    actions: actionsReducer,
    actionsUi: actionsUiReducer,
    actionFolder: actionFolderReducer,

    // Workflow folders
    workflowFolder: workflowFolderReducer,
    workflows: workflowsReducer,

    // Ticket management
    ticketStatuses: ticketStatusesReducer,
    ticketPriorities: ticketPrioritiesReducer,
    ticket: ticketsReducer,
    ticketPages: ticketPagesReducer,

    // Additional slices
    cannedResponses: cannedResponsesReducer,
    chatbots: chatbotsReducer,
    inbox: inboxReducer,
    copilotProfiles: copilotProfilesReducer,
    copilot: copilotReducer,
    copilotChat: copilotChatReducer,
    notification: notificationReducer,
    autoReply: autoReplyReducer,
    workspaceSettings: workspaceSettingsReducer,
    agentStatuses: agentStatusesReducer,
    conversations: conversationsReducer,
    emailTemplates: emailTemplatesReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
        ignoredPaths: ['some.nested.path'],
      },
    }),
  devTools: process.env.NODE_ENV !== 'production',
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
